"use client";

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";
import { injectMetaTags } from "@/lib/seoHelpers";
import { saltAirSections } from "@/mocks/blogSaltAir";

export default function SaltAirArticlePage() {
  const { t, currentLang } = useLanguage();
  useHreflang();
  const isEs = currentLang === "es";

  useEffect(() => {
    const titleEn = "Why Miami Window Hardware Fails \u2014 Salt Air Effects";
    const titleEs = "Por Qu\u00E9 Fallan los Herrajes en Miami \u2014 Efectos del Aire Salino";
    document.title = isEs
      ? `${titleEs} \u2014 Windows and Doors Repair LLC Miami`
      : `${titleEn} \u2014 Windows and Doors Repair LLC Miami`;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", String(t("blog.salt.metaDesc") ?? ""));

    const cleanupSeo = injectMetaTags({
      title: isEs ? titleEs : titleEn,
      description: String(t("blog.salt.metaDesc") ?? ""),
      url: isEs
        ? "https://impact-window-repair-miami.com/es/blog/aire-salino-herrajes-miami/"
        : "https://impact-window-repair-miami.com/blog/salt-air-hardware-failure/",
      image: "https://impact-window-repair-miami.com/og/og-blog.jpg",
      imageAlt: isEs ? "Efectos del aire salino en herrajes de ventanas Miami" : "Miami salt air window hardware effects",
      breadcrumbs: [
        {
          name: isEs ? "Inicio" : "Home",
          item: "https://impact-window-repair-miami.com/",
        },
        {
          name: "Blog",
          item: "https://impact-window-repair-miami.com/blog/",
        },
        {
          name: isEs ? "Mantenimiento" : "Maintenance",
          item: isEs
            ? "https://impact-window-repair-miami.com/es/blog/aire-salino-herrajes-miami/"
            : "https://impact-window-repair-miami.com/blog/salt-air-hardware-failure/",
        },
      ],
      type: "article",
      articleAuthor: "Windows and Doors Repair LLC",
      articlePublisher: "https://impact-window-repair-miami.com",
      articleSection: isEs ? "Mantenimiento" : "Maintenance",
      articlePublishedTime: "2026-05-18",
      articleModifiedTime: "2026-05-18",
    });

    const schema = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: String(t("blog.salt.h1") ?? ""),
      author: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      datePublished: "2026-05-18",
      dateModified: "2026-05-18",
      publisher: {
        "@type": "Organization",
        name: "Windows and Doors Repair LLC",
        telephone: "+17863774452",
        address: {
          "@type": "PostalAddress",
          streetAddress: "848 Brickell Ave, PH-5",
          addressLocality: "Miami",
          addressRegion: "FL",
          postalCode: "33131",
          addressCountry: "US",
        },
      },
      description: String(t("blog.salt.metaDesc") ?? ""),
      inLanguage: isEs ? "es-US" : "en-US",
    };

    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "salt-article-schema";
    s.text = JSON.stringify(schema);
    document.head.appendChild(s);

    return () => {
      cleanupSeo();
      const el = document.getElementById("salt-article-schema");
      if (el) document.head.removeChild(el);
    };
  }, [isEs, t]);

  return (
    <main className="bg-white">
      <div className="mx-auto max-w-5xl px-6">
        {/* Article Header */}
        <section className="py-12">
          <nav className="mb-4 flex items-center gap-2 text-sm text-gray-400">
            <Link to="/blog/" className="text-[#047857] hover:text-[#ea580c]">
              {isEs ? "Blog" : "Blog"}
            </Link>
            <span>&gt;</span>
            <span>{isEs ? "Mantenimiento" : "Maintenance"}</span>
          </nav>

          <span className="inline-block rounded bg-[#fbbf24] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#064e3b]">
            {isEs ? "Mantenimiento" : "Maintenance"}
          </span>

          <h1 className="mt-4 text-4xl font-bold leading-tight text-[#064e3b]">
            {t("blog.salt.h1")}
          </h1>

          <p className="mt-4 text-sm text-gray-500">
            {isEs ? "Publicado mayo 2026 \u00B7 8 min de lectura" : "Published May 2026 \u00B7 8 min read"}
          </p>

          <img
            src="https://placehold.co/1000x500/064e3b/fbbf24?text=Miami+Salt+Air+Hardware"
            alt={isEs ? "Herrajes de ventanas afectados por aire salino en Miami" : "Window hardware affected by Miami salt air"}
            className="mt-8 w-full rounded-xl object-cover"
            width={1000}
            height={500}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://placehold.co/1000x500/064e3b/fbbf24?text=Salt+Air+Article";
            }}
          />
        </section>

        {/* Disclaimer */}
        <div className="mb-10 rounded-r-xl border-l-4 border-[#ea580c] bg-[#ea580c]/10 p-6">
          <p className="text-sm font-semibold leading-relaxed text-[#064e3b]">
            {isEs
              ? "Este art\u00EDculo cubre reparaci\u00F3n y mantenimiento de herrajes solamente. No somos contratistas con licencia en Florida para reemplazos de ventanas con impacto ni trabajo HVHZ."
              : "This article covers hardware repair and maintenance only. We are not a licensed Florida contractor for impact window replacement or HVHZ work."}
          </p>
        </div>

        {/* Intro */}
        <section className="mb-12">
          <p className="mb-4 text-base leading-[1.7] text-[#374151]">
            {t("blog.salt.intro")}
          </p>
        </section>

        {/* Article Sections */}
        {saltAirSections.map((section) => (
          <section key={section.key} className="mb-12">
            <h2 className="mb-3 text-2xl font-bold text-[#064e3b]">
              {t(`blog.salt.h2${section.key.charAt(0).toUpperCase() + section.key.slice(1)}`)}
            </h2>
            <p className="text-base leading-[1.7] text-[#374151]">
              {isEs ? section.bodyEs : section.bodyEn}
            </p>
          </section>
        ))}

        {/* CTA Section */}
        <section className="my-12 -mx-6 bg-[#ea580c] px-6 py-16 text-white">
          <div className="mx-auto max-w-5xl text-center">
            <h2 className="text-3xl font-extrabold md:text-4xl">
              {t("blog.salt.h2Cta")}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-white/90">
              {isEs
                ? "Una inspecci\u00F3n de 20 minutos puede identificar corrosi\u00F3n temprana antes de que cause fallas costosas. Recomendado para casas costeras cada 6-8 meses."
                : "A 20-minute inspection can identify early corrosion before it causes costly failures. Recommended for coastal homes every 6-8 months."}
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <a
                href="tel:+17863774452"
                className="inline-block whitespace-nowrap rounded-lg bg-white px-8 py-3 font-bold text-[#ea580c] transition hover:bg-[#fefce8]"
              >
                {isEs ? "Llame al (786) 377-4452" : "Call (786) 377-4452"}
              </a>
              <Link
                to="/legal-disclaimer/"
                className="inline-block whitespace-nowrap rounded-lg border-2 border-white px-8 py-3 font-bold text-white transition hover:bg-white hover:text-[#ea580c]"
              >
                {t("blog.cta.button")}
              </Link>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-12">
          <h2 className="mb-6 text-2xl font-bold text-[#064e3b]">
            {t("blog.relatedArticles")}
          </h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <Link
              to="/blog/window-repair-cost-miami/"
              className="block rounded-lg border border-[#064e3b]/10 bg-white p-5 transition hover:border-[#fbbf24]/50 hover:shadow-lg"
            >
              <span className="inline-block rounded bg-[#fbbf24] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#064e3b]">
                {t("blog.catPricing")}
              </span>
              <h3 className="mt-3 text-lg font-bold leading-snug text-[#064e3b]">
                {t("blog.articleCost.title")}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                {t("blog.articleCost.excerpt")}
              </p>
              <span className="mt-4 inline-block text-sm font-medium text-[#047857] hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
            <Link
              to="/blog/impact-windows-vs-shutters/"
              className="block rounded-lg border border-[#064e3b]/10 bg-white p-5 transition hover:border-[#fbbf24]/50 hover:shadow-lg"
            >
              <span className="inline-block rounded bg-[#fbbf24] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#064e3b]">
                {t("blog.catComparison")}
              </span>
              <h3 className="mt-3 text-lg font-bold leading-snug text-[#064e3b]">
                {t("blog.articleShutters.title")}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                {t("blog.articleShutters.excerpt")}
              </p>
              <span className="mt-4 inline-block text-sm font-medium text-[#047857] hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
            <Link
              to="/blog/insurance-discount-impact-windows/"
              className="block rounded-lg border border-[#064e3b]/10 bg-white p-5 transition hover:border-[#fbbf24]/50 hover:shadow-lg"
            >
              <span className="inline-block rounded bg-[#fbbf24] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#064e3b]">
                {t("blog.catInsurance")}
              </span>
              <h3 className="mt-3 text-lg font-bold leading-snug text-[#064e3b]">
                {t("blog.articleInsurance.title")}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-600">
                {t("blog.articleInsurance.excerpt")}
              </p>
              <span className="mt-4 inline-block text-sm font-medium text-[#047857] hover:text-[#ea580c]">
                {t("blog.readMore")}
              </span>
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}